import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { ViewIcon, EllipsesIcon, DuplicateIcon } from 'foundations-assets';
import { useBehavior } from '../../../extensions/hooks/useBehavior';
import { ChickletHoverActions } from '../../../extensions/constants/BehaviorTypes';
import { useAssociatedObjectsCardRedesignContext } from '../../../context/AssociatedObjectsCardRedesignContext';
import { PRIMARY_STATE, SECONDARY_STATE } from '../../../types/ChickletHoverStates';
import { ListItem } from 'foundations-components/transitional/components/List';
import { List } from 'foundations-components/transitional/components/List';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { View } from 'foundations-components/components/View';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { UPDATE_ASSOCIATION_MODAL_STATES } from 'crm-message-bus/types/UpdateAssociationModalStates';
import { useCallback, useMemo } from 'react';
import { getIsCompanyPrimaryAssociation } from '../../../helpers/objectUtilFunction';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Button } from 'foundations-components/transitional/components/Button';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function AssociationChicletActions({
  association
}) {
  var _results$map;
  const {
    total,
    associationDefinition,
    fromObjectTypeId,
    fromObjectId,
    hasScope,
    isUngated,
    onPreview,
    readOnly,
    userPermissions,
    results
  } = useAssociatedObjectsCardRedesignContext();

  // TODO: Investigate the additional logic that needs to be included here
  // https://git.hubteam.com/HubSpot/crm-records/issues/1493
  const canEditAssociation = userPermissions.currentUserCanEditAssociations && !readOnly;
  const sendOpenUpdateAssociationModal = useSendCrmMessageTopic('OPEN_UPDATE_ASSOCIATION_MODAL');
  const isPrimary = Boolean(association.labels.primaryLabel);
  const primaryAssociationObjectId = useMemo(() => {
    const primaryAssociation = results === null || results === void 0 ? void 0 : results.find(result => Boolean(result.labels.primaryLabel));
    return primaryAssociation ? primaryAssociation.objectId : undefined;
  }, [results]);
  const handleOpenPrimaryModal = useCallback(() => {
    let modalState;
    if (isPrimary) {
      modalState = UPDATE_ASSOCIATION_MODAL_STATES.PRIMARY_MODAL_PRIMARY_STATE;
    } else if (primaryAssociationObjectId) {
      modalState = UPDATE_ASSOCIATION_MODAL_STATES.PRIMARY_MODAL_SECONDARY_PRIMARY_EXISTS_STATE;
    } else {
      modalState = UPDATE_ASSOCIATION_MODAL_STATES.PRIMARY_MODAL_SECONDARY_ASSOCIATION_PRIMARY_DOESNT_EXIST_STATE;
    }
    sendOpenUpdateAssociationModal({
      currentState: modalState,
      fromObjectId,
      fromObjectTypeId,
      toObjectId: association.objectId,
      toObjectTypeId: association.objectTypeId
    });
  }, [isPrimary, primaryAssociationObjectId, sendOpenUpdateAssociationModal, fromObjectId, fromObjectTypeId, association.objectId, association.objectTypeId]);
  const handleOpenEditAssociationModal = useCallback(() => {
    sendOpenUpdateAssociationModal({
      currentState: UPDATE_ASSOCIATION_MODAL_STATES.EDIT_ASSOCIATION_MODAL_STATE,
      fromObjectId,
      fromObjectTypeId,
      toObjectId: association.objectId,
      toObjectTypeId: association.objectTypeId
    });
  }, [sendOpenUpdateAssociationModal, fromObjectId, fromObjectTypeId, association.objectId, association.objectTypeId]);
  const isCompanyPrimaryAssociation = getIsCompanyPrimaryAssociation(fromObjectTypeId, association.objectTypeId, associationDefinition);
  const handleOpenRemoveAssociationModal = useCallback(() => {
    let modalState;
    if (isPrimary) {
      if (total === 1) {
        modalState = UPDATE_ASSOCIATION_MODAL_STATES.REMOVE_ASSOCIATION_MODAL_PRIMARY_ONLY_ASSOCIATION_STATE;
      } else {
        modalState = UPDATE_ASSOCIATION_MODAL_STATES.REMOVE_PRIMARY_ASSOCIATION_MODAL_STATE;
      }
    } else {
      if (isCompanyPrimaryAssociation) {
        modalState = UPDATE_ASSOCIATION_MODAL_STATES.REMOVE_REVERSE_PRIMARY_MODAL_STATE;
      } else {
        modalState = UPDATE_ASSOCIATION_MODAL_STATES.REMOVE_SECONDARY_ASSOCIATION_MODAL_STATE;
      }
    }
    sendOpenUpdateAssociationModal({
      currentState: modalState,
      fromObjectId,
      fromObjectTypeId,
      toObjectId: association.objectId,
      toObjectTypeId: association.objectTypeId
    });
  }, [isPrimary, sendOpenUpdateAssociationModal, fromObjectId, fromObjectTypeId, association.objectId, association.objectTypeId, total, isCompanyPrimaryAssociation]);
  const useActions = useBehavior(ChickletHoverActions, association.objectTypeId, hasScope, isUngated);
  const {
    PrimaryAction,
    SecondaryActions
  } = useActions({
    associatedObjectsIds: (_results$map = results === null || results === void 0 ? void 0 : results.map(result => result.objectId)) !== null && _results$map !== void 0 ? _results$map : [],
    canEdit: canEditAssociation,
    canView: true,
    currentState: isPrimary ? PRIMARY_STATE : SECONDARY_STATE,
    fromObjectTypeId,
    handleOpenEditAssociationModal,
    handleOpenPrimaryModal,
    handleOpenRemoveAssociationModal,
    objectId: association.objectId,
    onPreview,
    primaryAssociationObjectId,
    toObjectTypeId: association.objectTypeId,
    fromObjectId,
    associationDefinition,
    isUngated,
    hasScope
  });
  const filteredSecondaryActions = SecondaryActions.filter(action => action !== undefined && action !== null);
  const dropdownButton = useCallback(props => {
    return /*#__PURE__*/_jsx(IconButton, Object.assign({
      "aria-label": "More actions",
      size: "extra-small",
      use: "tertiary-light"
    }, props, {
      children: /*#__PURE__*/_jsx(EllipsesIcon, {})
    }));
  }, []);
  const primaryAction = useMemo(() => {
    if (!PrimaryAction) {
      return null;
    }
    if (PrimaryAction.copyValue) {
      // The CopyButton and IconButton sizes are different. I reached out to trellis here https://hubspot.slack.com/archives/C1WG3L3J6/p1751912786438099
      return /*#__PURE__*/_jsx(CopyButton, {
        size: "xs",
        value: PrimaryAction.copyValue,
        disabled: PrimaryAction.disabled,
        "aria-label": typeof PrimaryAction.label === 'string' ? PrimaryAction.label : undefined,
        "data-selenium-test": PrimaryAction.dataSeleniumTest,
        "data-test-id": PrimaryAction.dataTestId,
        href: PrimaryAction.href,
        onClick: PrimaryAction.onClick,
        children: /*#__PURE__*/_jsx(DuplicateIcon, {})
      });
    }
    return /*#__PURE__*/_jsx(IconButton, {
      "data-selenium-test": PrimaryAction.dataSeleniumTest,
      "data-test-id": PrimaryAction.dataTestId,
      disabled: PrimaryAction.disabled,
      href: PrimaryAction.href,
      onClick: PrimaryAction.onClick,
      placement: "left",
      size: "xs",
      tooltip: PrimaryAction.disabled && PrimaryAction.disabledMessage,
      "aria-label": typeof PrimaryAction.label === 'string' ? PrimaryAction.label : undefined,
      children: /*#__PURE__*/_jsx(ViewIcon, {})
    });
  }, [PrimaryAction]);
  const renderSecondaryAction = useCallback(actionProps => {
    if (typeof actionProps.label !== 'string') {
      return actionProps.label;
    }
    return /*#__PURE__*/_jsx(Tooltip, {
      title: (actionProps === null || actionProps === void 0 ? void 0 : actionProps.disabled) && (actionProps === null || actionProps === void 0 ? void 0 : actionProps.disabledMessage),
      disabled: !(actionProps !== null && actionProps !== void 0 && actionProps.disabled),
      placement: "left",
      children: /*#__PURE__*/_jsx(Button, {
        "data-selenium-test": actionProps === null || actionProps === void 0 ? void 0 : actionProps.dataSeleniumTest,
        "data-test-id": actionProps === null || actionProps === void 0 ? void 0 : actionProps.dataTestId,
        href: actionProps === null || actionProps === void 0 ? void 0 : actionProps.href,
        external: !!(actionProps !== null && actionProps !== void 0 && actionProps.href),
        onClick: actionProps === null || actionProps === void 0 ? void 0 : actionProps.onClick,
        use: "tertiary",
        size: "xs",
        disabled: actionProps === null || actionProps === void 0 ? void 0 : actionProps.disabled,
        children: actionProps === null || actionProps === void 0 ? void 0 : actionProps.label
      })
    });
  }, []);
  return /*#__PURE__*/_jsxs(View, {
    flexWrap: "nowrap",
    children: [primaryAction, canEditAssociation && filteredSecondaryActions.length > 0 && /*#__PURE__*/_jsx(Dropdown, {
      Button: dropdownButton,
      buttonSize: "extra-small",
      "data-selenium-test": "chicklet-more-btn",
      "data-test-id": "chicklet-more-btn",
      placement: "bottom left",
      marginInlineStart: "200",
      children: /*#__PURE__*/_jsx(List, {
        children: filteredSecondaryActions.map((actionProps, index) => {
          return /*#__PURE__*/_jsx(ListItem, {
            children: renderSecondaryAction(actionProps)
          }, index);
        })
      })
    })]
  });
}
export default AssociationChicletActions;